import React, { useMemo, useState, useEffect, useRef } from 'react'
import moment from 'moment-timezone'
import { Calendar, Views, momentLocalizer } from 'react-big-calendar'
import { useFetchData } from '../utils/useFetchData';
import EasyCalToolbar from './EasyCalToolbar';
import EasyCalEvent from './EasyCalEvent';
import EasyCalEventWrapper from './EasyCalEventWrapper';
import EventModal from './EventModal';
import EasyCalWeek from './Views/EasyCalWeek';
import EasyCalTiles from './Views/EasyCalTiles';
import EasyCalAgenda from './Views/EasyCalAgenda';

const localizer = momentLocalizer(moment)
const eventAPI  = '/wp-json/wp/v2/ec_event?_embed&per_page=100';
const locationAPI = '/wp-json/wp/v2/event_location';
const categoryAPI = '/wp-json/wp/v2/event_type';

const EasyCalendar = (props) => {

  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

  const min = new Date(); min.setHours(6, 0, 0);
  const {views} = useMemo(() => ({
    views: {
      month: true,
      agenda: EasyCalAgenda
    },
  }), []);
  // const initialView = useState(window.innerWidth >= 768 ? 'month' : 'agenda');
  const initialView = isDesktop ? 'month' : 'agenda';
  // const [view, setView] = useState(Views.MONTH);
  const [view, setView] = useState('month');
  const [defaultDate, setDefaultDate] = useState(new Date());
  const { data:events, loading:eventLoading, error:eventError } = useFetchData(eventAPI);
  const { data:locations, loading:locationLoading, error:locationError } = useFetchData(locationAPI);
  const { data:categories, loading:catLoading, error:catError } = useFetchData(categoryAPI);

  const urlParams   = new URLSearchParams(window.location.search);
  const locationId  = urlParams.get('location'); // Get the location ID from the URL
  const eventId     = urlParams.get('event'); // Get the category ID from the URL
  const formats = {
    weekdayFormat: 'dddd',
  }

  const [filterLocation, setLocation] = useState(0); // State for location filter
  const [filterCategory, setCategory] = useState(0); // State for category filter
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if(locationId && locations)
      if(locations.find(location => location.id == locationId)) {
        setLocation(Number(locationId));
      }
  }, [locations, locationId]);

  const filteredEvents = useMemo(() => {
    if (!events)
      return [];
    let newEvents = events;

    if(filterLocation || filterCategory) {
      newEvents = events.filter((event) => {
        return (
          (event.location == filterLocation || filterLocation == 0) &&
          (event.category.includes(filterCategory) || filterCategory == 0)
        );
      });
    }

    if (eventId)
      newEvents = newEvents.filter(event => event.id == eventId);

    const earliestEvent = newEvents.reduce((earliest, event) => {
      return (!earliest || event.start < earliest.start) ? event : earliest;
    }, null);

    // if((earliestEvent && filterLocation != 0) || (earliestEvent && eventId)) {
    //   let newDate = new Date(defaultDate.setMonth(earliestEvent.start.getMonth()));
    //   let today = new Date();
    //   console.log("newdate/today: ");
    //   console.log(newDate);
    //   console.log(today);
      
    //   if(newDate > today)
    //     setDefaultDate(newDate);
    // }
    return newEvents;
  }, [events, filterLocation, filterCategory, eventId]);

  useEffect(() => {
    if(eventId)
      handleSelectEvent(filteredEvents[0]); // there's only gonna be one filtered event if we've got an event ID
  }, [filteredEvents]);

  const locationLookup = useMemo(() => {
    if(!locations)
      return
    return locations.reduce((acc, location) => {
      acc[location.id] = location.name;
      return acc;
    }, {});
  }, [locations]);

  const categoryLookup = useMemo(() => {
    if(!categories)
      return;
    return categories.reduce((acc, category) => {
      acc[category.id] = category.name;
      return acc;
    }, {});
  }, [categories]);

  // Rest URL filters after action in the calendar
  const updateURL = () => {
    const url = new URL(window.location);
    if(locationId)
      url.searchParams.delete('location');
    if(eventId)
      url.searchParams.delete('event');
    window.history.pushState({}, '', url);
  };
  
  // Handle location filter change
  const handleLocationChange = (value) => {
    updateURL();
    setLocation(Number(value));
  };

  // Handle category filter change
  const handleCategoryChange = (value) => {
    updateURL();
    setCategory(Number(value));
  };

  // Reset filters
  const resetFilter = () => {
    updateURL();
    setLocation(0);
    setCategory(0);
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
    updateURL();
  };

  const components = {
    toolbar: (props) => (
      <EasyCalToolbar
      {...props}
      events={events}
      filterLocation={filterLocation}
      filterCategory={filterCategory}
      onLocationChange={handleLocationChange}
      onCategoryChange={handleCategoryChange}
      onResetFilter={resetFilter}
    />
    ),
    event: (props) => (
    <EasyCalEvent  
    {...props}
    locationLookup={locationLookup}
    />),
    eventWrapper: (props) => (
      <EasyCalEventWrapper
      {...props}
      view={view}
      locationLookup={locationLookup}
      />),
  }

  // API Errors and Loading 
  if(eventError)
    return ( <div className='calendarWait'><p>Event Error: {eventError.message}</p></div> )
  if(eventLoading) 
    return ( <div className='calendarWait'><img src="/wp-content/uploads/2026/05/mammogram-truck.gif"/></div> )
    // return ( <div className='calendarWait'><p>Loading: {eventLoading}</p></div> )
  if(locationError)
    return ( <div className='calendarWait'><p>location Error: {locationError.message}</p></div> )
  if(locationLoading)
    return ( <div className='calendarWait'><p>Loading: {locationLoading}</p></div> )
  if(catError)
    return ( <div className='calendarWait'><p>cat Error: {catError.message}</p></div> )
  if(catLoading) 
    return ( <div className='calendarWait'><p>Loading: {catLoading}</p> </div> )

  return(
    <div className="height600" {...props}>
      <Calendar
        components={components}
        defaultDate={defaultDate}
        defaultView={initialView}
        // defaultView='agenda'
        // defaultView='week'
        // defaultView='tiles'
        events={filteredEvents}
        localizer={localizer}
        formats={formats}
        startAccessor="start"
        endAccessor="end"
        min={min}
        step={60}
        views={views}
        onView={(newView) => setView(newView)} // Update the view state
        // view={view}
        onSelectEvent={handleSelectEvent} // Triggered when an event is clicked
      />
      {selectedEvent && locationLookup && categoryLookup && (
        <EventModal 
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          event={selectedEvent} 
          locationLookup={locationLookup} 
          categoryLookup={categoryLookup} 
      />
      )}
    </div>
  )
}

export default EasyCalendar;